import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { layoutReviewFlow } from "../../lib/reviewFlow.js";
import { flowRoutes } from "../../lib/reviewFlowRoutes.js";
import { ACTION_CHIP, FILE_LABEL } from "./triageParts.jsx";
import { CAPTION_H, STEP_INK, NodeLabel, PartLabel, useInks, alpha, useCanvas, useReveal, clientRect, hash } from "./ReviewFlowParts.jsx";

// The Findings flow laid out as the Sankey lays it out (lib/reviewFlow.js:
// the same columns, nodes, stacked parts and labels, pointed at and opened the
// same way) but drawn as the Convergence draws: every link a bundle of
// hairlines, one per finding (or per `unit` of them, said in the corner),
// warming step by step toward what needs a person, which runs red. What a
// step let go fades as it goes.

const REVEAL_MS = 1100;
const BAR = 3;
const GAP = 10;
const LANE = 150;
const LABEL_H = 16;
const SEGMENT_OPACITY = [0.55, 0.38, 0.24];
const INKS = { sand: STEP_INK.written, kept: STEP_INK.kept, triaged: STEP_INK.triaged, met: STEP_INK.met, muted: "var(--muted)", ink: "var(--ink)" };
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// A node's ink, as the Sankey colours it.
const inkKey = (node) => (node?.kind === "drop" ? "muted" : node?.flag || node?.id === "met" ? "met" : node?.id === "written" ? "sand" : node?.id === "kept" ? "kept" : node?.id === "triaged" ? "triaged" : "muted");

// The y of a level-ended cubic edge at x.
function edgeY(xa, a, xb, b, x) {
  if (xb <= xa) return a;
  const d = (xb - xa) / 2;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 14; i++) {
    const t = (lo + hi) / 2;
    const u = 1 - t;
    const xt = u * u * u * xa + 3 * u * u * t * (xa + d) + 3 * u * t * t * (xb - d) + t * t * t * xb;
    if (xt < x) lo = t; else hi = t;
  }
  const t = (lo + hi) / 2;
  const u = 1 - t;
  return u * u * u * a + 3 * u * u * t * a + 3 * u * t * t * b + t * t * t * b;
}

function layoutGrouped(model, W, H) {
  const geo = layoutReviewFlow(model, { width: W, height: H, bar: BAR, gap: GAP, lane: LANE, labelHeight: LABEL_H });
  const out = { geo, links: [], unit: 1, liftUnit: 1 };
  if (!geo.columns.length || !model?.links?.length) return out;
  const { unit, liftUnit } = flowRoutes(model);
  Object.assign(out, { unit, liftUnit });
  // Each link's slice of its parent, child by child, as the Sankey cuts it.
  const order = new Map();
  for (const col of model.columns) col.nodes.forEach((id, i) => order.set(id, i));
  const byParent = new Map();
  for (const l of model.links) {
    if (!byParent.has(l.parent)) byParent.set(l.parent, []);
    byParent.get(l.parent).push(l);
  }
  let seed = 0;
  for (const [parentId, list] of byParent) {
    const p = geo.nodes.get(parentId);
    if (!p) continue;
    const ordered = [...list].sort((a, b) => order.get(a.child) - order.get(b.child));
    const total = ordered.reduce((n, l) => n + l.value, 0);
    let a = p.y;
    for (const l of ordered) {
      const c = geo.nodes.get(l.child);
      if (!c || !(total > 0)) continue;
      const a1 = a + (l.value / total) * p.h;
      const child = model.nodes.get(l.child);
      const red = child?.flag || l.child === "met";
      const marks = clamp(Math.round(l.value / (red ? liftUnit : unit)), 1, Math.max(1, l.value));
      // A stacked child takes each line into the part it belongs to.
      const parts = c.segments || null;
      const lines = [];
      for (let j = 0; j < marks; j++) {
        const ya = a + ((a1 - a) * (j + 0.5)) / marks;
        let yb = c.y + (c.h * (j + 0.5)) / marks;
        if (parts?.length) {
          let at = ((j + 0.5) / marks) * child.count;
          const seg = parts.find((s) => { if (at <= s.count) return true; at -= s.count; return false; }) || parts[parts.length - 1];
          yb = seg.y + seg.h * clamp(at / Math.max(1, seg.count), 0, 1);
        }
        const k1 = 0.5 + (hash(seed, 1) - 0.5) * 0.22;
        const k2 = 0.5 + (hash(seed, 2) - 0.5) * 0.22;
        seed += 1;
        lines.push({ ya, yb, k1, k2 });
      }
      out.links.push({ id: `${l.parent}>${l.child}`, parent: l.parent, child: l.child, value: l.value, x0: p.x + p.w, x1: c.x, a, a1, b: c.y, b1: c.y + c.h, lines, red, drop: child?.kind === "drop" });
      a = a1;
    }
  }
  return out;
}

export default function ReviewFlowGrouped({
  model, width, height, point = null, onPoint, onLeave, onOpen, caption, poolNote = "", onOpenLite = null,
}) {
  const boxRef = useRef(null);
  const g = useMemo(() => layoutGrouped(model, width, height), [model, width, height]);
  const inks = useInks(boxRef, INKS);
  const [canvasRef, getCtx] = useCanvas(width, height);
  const [started, setStarted] = useState(false);
  const [overLink, setOverLink] = useState(null);
  const pointed = point?.node || null;
  const { geo } = g;
  const lastIndex = geo.columns.length - 1;

  const keeps = useCallback((l) => !pointed || l.parent === pointed || l.child === pointed, [pointed]);
  const nodeLit = (id) => !pointed || id === pointed || model.nodes.get(id)?.parent === pointed || model.nodes.get(pointed)?.parent === id;

  const paint = useCallback((p) => {
    const ctx = getCtx();
    if (!ctx || !inks || !g.links.length) return;
    if (p > 0 && !started) setStarted(true);
    const front = p >= 1 ? width : lerp(0, width, easeOut(p));
    const dark = document.documentElement.dataset.theme === "dark";
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, clamp(front, 0, width), height);
    ctx.clip();
    for (const l of g.links) {
      const parent = model.nodes.get(l.parent);
      const child = model.nodes.get(l.child);
      const on = keeps(l) || overLink === l;
      const focus = overLink ? overLink === l : pointed ? keeps(l) : false;
      // Hairlines stay hairlines: the more a link carries for its width, the
      // fainter each line, so a dense link reads as a sheen, not a slab.
      const per = Math.max(0.4, ((l.a1 - l.a) + (l.b1 - l.b)) / 2 / Math.max(1, l.lines.length));
      const base = l.red ? 0.95 : clamp(per * 0.55, 0.14, 0.7) * (l.drop ? 0.65 : 1);
      const k = (pointed || overLink) && !on ? 0.12 : focus ? 1.4 : 1;
      const gr = ctx.createLinearGradient(l.x0, 0, l.x1, 0);
      gr.addColorStop(0, alpha(inks[inkKey(parent)], base * k));
      gr.addColorStop(1, alpha(inks[inkKey(child)], (l.drop ? base * 0.25 : base) * k));
      if (l.red && dark) { ctx.shadowBlur = 8; ctx.shadowColor = alpha(inks.met, 0.7 * k); }
      ctx.strokeStyle = gr;
      ctx.lineWidth = l.red ? 1.3 : 0.7;
      ctx.beginPath();
      const dx = l.x1 - l.x0;
      for (const ln of l.lines) {
        ctx.moveTo(l.x0, ln.ya);
        ctx.bezierCurveTo(l.x0 + dx * ln.k1, ln.ya, l.x1 - dx * (1 - ln.k2), ln.yb, l.x1, ln.yb);
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
    // Bars, as the Sankey's but thin.
    for (const n of geo.nodes.values()) {
      const node = model.nodes.get(n.id);
      if (!node || !(n.h > 0)) continue;
      const lit = nodeLit(n.id) ? 1 : 0.35;
      if (n.segments) {
        n.segments.forEach((seg, i) => {
          ctx.fillStyle = alpha(inks[inkKey(node)], (SEGMENT_OPACITY[i] ?? 0.2) * lit);
          ctx.fillRect(n.x, seg.y, BAR, Math.max(0, seg.h - (i < n.segments.length - 1 ? 1 : 0)));
        });
      } else {
        ctx.fillStyle = alpha(inks[inkKey(node)], (node.kind === "drop" ? 0.45 : 1) * lit);
        if (node.flag && dark) { ctx.shadowBlur = 10; ctx.shadowColor = alpha(inks.met, 0.8 * lit); }
        ctx.fillRect(n.x, n.y, BAR, Math.max(1, n.h));
        ctx.shadowBlur = 0;
      }
    }
    ctx.restore();
  }, [g, geo, inks, getCtx, started, keeps, overLink, pointed, model, width, height]);

  const signature = g.links.length ? g.links.map((l) => `${l.id}:${l.value}`).join(",") : "";
  const { done } = useReveal(inks ? signature : "", REVEAL_MS, paint);
  useLayoutEffect(() => { if (done.current) paint(1); }, [paint, done]);

  // What the pointer is on: a bar, else the link whose hairlines hold it.
  const at = (e) => { const r = e.currentTarget.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const barAt = (x, y) => [...geo.nodes.values()].find((n) => x >= n.x - 4 && x <= n.x + BAR + 4 && y >= n.y - 2 && y <= n.y + n.h + 2) || null;
  const linkAt = (x, y) => g.links.find((l) => x > l.x0 && x < l.x1 && y >= edgeY(l.x0, l.a, l.x1, l.b, x) - 1.5 && y <= edgeY(l.x0, l.a1, l.x1, l.b1, x) + 1.5) || null;
  const onMove = (e) => {
    const [x, y] = at(e);
    const bar = barAt(x, y);
    const link = bar ? null : linkAt(x, y);
    e.currentTarget.style.cursor = bar || link ? "pointer" : "default";
    if (link !== overLink) setOverLink(link);
    const id = bar ? bar.id : link ? link.child : null;
    if (!id) { if (pointed) onLeave(); return; }
    if (id !== pointed) {
      const n = geo.nodes.get(id);
      onPoint(id, clientRect(e.currentTarget, n.x, n.y, BAR, Math.max(4, n.h)));
    }
  };
  const onOut = () => { setOverLink(null); if (pointed) onLeave(); };
  const onClick = (e) => {
    const [x, y] = at(e);
    const bar = barAt(x, y);
    const link = bar ? null : linkAt(x, y);
    const id = bar ? bar.id : link ? link.child : null;
    if (id) onOpen(id);
  };

  const cols = geo.columns;
  const step = cols.length > 1 ? cols[1].x - cols[0].x : 0;
  const delay = (x) => Math.round((Math.max(0, x) / Math.max(1, width)) * REVEAL_MS * 0.9);
  const hidden = !started;
  const point$ = (id) => (e) => onPoint(id, e.currentTarget.getBoundingClientRect());
  const xOf = (id) => cols.find((c) => c.id === id)?.x ?? 0;
  const columnOf = (id) => model.columns.find((c) => c.id === id) || null;
  const triageEmpty = !columnOf("triage")?.nodes.length;
  const standardEmpty = !triageEmpty && !columnOf("standard")?.nodes.length;
  const top = CAPTION_H;

  return (
    <div ref={boxRef} data-flow-view="grouped" className="absolute inset-0">
      {width > 0 ? cols.map((c) => (
        <span key={c.id} data-flow-caption={c.id} className={`${FILE_LABEL} absolute top-0 whitespace-nowrap`} style={{ left: c.x }}>{caption(c.id)}</span>
      )) : null}
      {g.unit > 1 ? (
        <span data-flow-unit="" className="absolute right-0 text-[9px] leading-[1.4] tracking-[0.02em] text-[color:var(--muted-soft)]" style={{ top: top + height - 12 }}>
          each line is {g.unit.toLocaleString()} findings{g.liftUnit === 1 ? "; what needs you, one each" : `; what needs you, ${g.liftUnit.toLocaleString()} each`}
        </span>
      ) : null}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute left-0"
        style={{ top, width, height }}
        onPointerMove={onMove}
        onPointerLeave={onOut}
        onClick={onClick}
      />
      {width > 0 ? [...geo.nodes.values()].map((n) => {
        const node = model.nodes.get(n.id);
        if (!node) return null;
        const last = n.col === lastIndex;
        const maxWidth = last ? LANE - 6 : Math.max(60, Math.min(170, step - n.w - 12));
        if (n.segments) {
          return n.segments.map((seg) => (
            <PartLabel key={`${n.id}#${seg.id}`} node={node} seg={seg} left={n.x + BAR + 4} maxWidth={maxWidth} dim={!nodeLit(n.id)} hidden={hidden} delay={delay(n.x)}
              onPoint={point$(n.id)} onLeave={onLeave} onOpen={() => onOpen(n.id)} />
          ));
        }
        return (
          <NodeLabel key={n.id} node={node} lay={n} left={n.x + BAR + 4} maxWidth={maxWidth} dim={!nodeLit(n.id)} hidden={hidden} delay={delay(n.x)}
            onPoint={point$(n.id)} onLeave={onLeave} onOpen={() => onOpen(n.id)} />
        );
      }) : null}
      {width > 0 && triageEmpty && poolNote ? (
        <span data-flow-note="pool" className={`${NOTE_CLASS} absolute`} style={{ left: xOf("triage"), maxWidth: Math.max(120, width - xOf("triage")), top: top + height / 2 - 12 }}>{poolNote}</span>
      ) : null}
      {width > 0 && standardEmpty ? (
        <span data-flow-note="untriaged" className="absolute grid justify-items-start gap-[6px]" style={{ left: xOf("standard"), maxWidth: Math.max(120, width - xOf("standard")), top: top + height / 2 - 22 }}>
          <span className={NOTE_CLASS}>Triage has not checked any of these yet.</span>
          {onOpenLite ? <button type="button" data-flow-open="lite" onClick={onOpenLite} className={ACTION_CHIP}>Open Triage</button> : null}
        </span>
      ) : null}
    </div>
  );
}
